// @ts-strict-ignore
/**
 * @module DirectorySearch
 */

import { Service } from 'rainbow-web-sdk/services/service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { DirectoryService } from 'rainbow-web-sdk/services/directory/directory.service';
import { OFFICE365_AD_SVC, Office365AdService } from 'rainbow-web-sdk/services/directory/office365ad.service';
import { PERSONAL_DIRECTORY_SVC, PersonalDirectoryService } from 'rainbow-web-sdk/services/directory/personal-directory.service';
import { COMPANY_DIRECTORY_SVC, CompanyDirectoryService } from 'rainbow-web-sdk/services/company/company-directory.service';
import { PHONEBOOK_SVC, PhonebookService } from 'rainbow-web-sdk/services/directory/phonebook.service';
import { User } from 'rainbow-web-sdk/models';

export const DIRECTORY_SEARCH_SVC = 'DirectorySearchService';

const SEARCH_LIMIT_DEF = 20;
const SEARCH_LIMIT_MAX = 100;

/**
 * List of different directories in which users or contacts can be searched by name.
 */
export enum DirectoryType {
    /**
     * Network of Rainbow users of the logged-in user
     * This directory type is more of a container than a directory an refers to the network of Rainbow users of the logged-in user.
     * This network also known as 'roster' contains users who have been explicitly invited by the logged-in user
     * to share their online status.
     * No profile feature is required to use this directory.
     * Any Rainbow user, including those belonging to the default company, can use this directory.
     */
    RAINBOW_USERS_NETWORK = "RAINBOW_USERS_NETWORK",

    /**
     * Directory for Rainbow Users.
     * No profile feature is required to use this directory.
     * Any Rainbow user, including those belonging to the default company, can use this directory.
     * The directory scope corresponds to all Rainbow public users and users being in companies visible to the logged-in user.
     * This visibility depends on that defined for the logged-in user and the company to which he belongs.
     */
    RAINBOW_USERS = "RAINBOW_USERS",

    /**
     * Directory for Rainbow Business Contacts.
     * No profile feature is required to use this directory.
     * However, a user of a default company cannot use this directory.
     * A search request will be rejected if this directory cannot be used.
     */
    RAINBOW_BUSINESS_CONTACTS_DIRECTORY = "RAINBOW_BUSINESS_CONTACTS_DIRECTORY",

    /**
     * Directory for Rainbow Personal Contacts.
     * This directory is available only if the user has the profile feature PERSONAL_DIRECTORY.
     * A user of a default company cannot use this directory either.
     * A search request will be rejected if this directory cannot be used.
     */
    RAINBOW_PERSONAL_CONTACTS_DIRECTORY = "RAINBOW_PERSONAL_CONTACTS_DIRECTORY",

    /**
     * Directory for PBX users.
     * This directory is available only if the user has the profile feature TELEPHONY_PHONE_BOOK.
     * A user of a default company cannot use this directory either.
     * A search request will be rejected if this directory cannot be used.
     */
    RAINBOW_PBX_USERS = "RAINBOW_PBX_USERS",

    /**
     * Directory for Microsoft Azure AD users/Microsoft O365 contacts.
     * This directory is available only if the user has the profile feature MSO365_DIRECTORY_SEARCH.
     * A user of a default company cannot use this directory either.
     * A search request will be rejected if this directory cannot be used.
     */
    MICROSOFT_O365_USERS = "MICROSOFT_O365_USERS",

    /**
     * Directory for Microsoft Outlook personal contacts.
     * This directory is available only if the user has the profile feature MSO365_DIRECTORY_SEARCH.
     * A user of a default company cannot use this directory either.
     * A search request will be rejected if this directory cannot be used.
     */
    MICROSOFT_OUTLOOK_CONTACTS = "MICROSOFT_OUTLOOK_CONTACTS"
}

/**
 * Specified how the company specified in the search option will be managed in the search scope.
 */
export enum CompanyScopeType {
    /**
     * Refine the search by specifying that only users in my company are being searched for.
     */
    MY_COMPANY = "MY_COMPANY",

    /**
     * Refine the search by excluding users from my company.
     */
    MY_COMPANY_EXCLUDED = "MY_COMPANY_EXCLUDED",
}

/**
 * List of parameters associated with the company filter option.
 */
export interface CompanyOptions {
    /**
     * This information indicates how the company criterion will be handled in the search field.
     * This option is managed differently depending on the directory type, as described below:
     *  - RAINBOW_USERS_NETWORK: This option is not available.
     *  - RAINBOW_USERS: All options defined by CompanyScopeType are available.
     *  - RAINBOW_BUSINESS_CONTACTS_DIRECTORY: Only MY_COMPANY scope type can be specified.
     *  - RAINBOW_PERSONAL_CONTACTS_DIRECTORY: This option is not available.
     *  - MICROSOFT_O365_USERS: This option is not available.
     *  - RAINBOW_PBX_USERS: This option is not available.
     *  - MICROSOFT_OUTLOOK_CONTACTS: This option is not available.
     *
     * If the option value is not compatible with the specified directory, the request will be rejected and an error logged.
     */
    companyScope: CompanyScopeType;
}

/**
 * List of options for refining the search and specifying the results to be provided.
 */
export interface DirectorySearchOptions {
    /**
     * The number of results to return.
     * Limit value must be in range [1..100]. Default value is 20.
     * If the value is not in the authorized range, the request will be rejected and an error logged.
     */
    limit?: number;

    /**
     * Allows to search the user/contact on extended fields. [default value=false]
     * Default value is false.
     * In addition to the name-fields used by a "basic" search, an extended search will be carried out on addition fields
     * such as the job title or the company name for example.
     * The complete list of fields used in a extended search is described below.
     * This extended search is only available to user having the feature SEARCH_BY_TAGS in their profile.
     * If this option is set but the user does not have the feature SEARCH_BY_TAGS, the option is ignored and the search is performed in simple mode.
     * Furthermore, a warning is logged.
     * This option is managed differently depending on the directory type, as described below:
     *  - RAINBOW_USERS_NETWORK:
     *    The extended search is not available.
     *  - RAINBOW_USERS:
     *    This extended search applies on the following fields: firstName, lastName, company name, job title, department and tags.
     *  - RAINBOW_BUSINESS_CONTACTS_DIRECTORY:
     *    This extended search applies on the following fields: firstName, lastName, company name, job title, department and tags.
     *  - RAINBOW_PERSONAL_CONTACTS_DIRECTORY:
     *    This extended search applies on the following fields: firstName, lastName, company name, job title, department and tags.
     *  - RAINBOW_PBX_USERS:
     *    The extended search is not available.
     *  - MICROSOFT_O365_USERS:
     *    This extended search applies on the following fields: firstName, lastName, company name, job title and department.
     *  - MICROSOFT_OUTLOOK_CONTACTS:
     *    The extended search is not available.
     */
    extendedMode?: boolean;

    /**
     * Company filter to refine the search to include or exclude users/contacts of the connected users's company.
     * Default value is null meaning that no company filter is specified.
     * This option is only valid for searches in RAINBOW_USERS and RAINBOW_BUSINESS_CONTACTS_DIRECTORY directories.
     * If the option is specified but not managed for the given directory type, it will be ignored.
     */
    company?: CompanyOptions;
}

/**
 * List of data provided as the result of a directory search.
 */
export interface DirectorySearchResults {
    /**
     * The list of users (as User model) matching the search criteria.
     */
    users: User[];

    /**
     * Indicates whether there are more results available.
     * If this flag is true and the contact/user searched for is not present in the results list, then the user can
     * - request more results (via 'limit' option) for the same query
     * - refine the search
     */
    moreResultsAvailable: boolean;
}

/**
 * List of data provided as results by an internal directory search service (users, Personal, Company, O365, PBX) implemented in the Web Client.
 * @internal
 */
export interface DirectoryInternalSearchResults {
    /**
     * contacts The list of items (as Contact model) matching the search criteria.
     */
    contacts: Contact[];

    /**
     * Total number of directory entries matching the criteria
     * If this total number is not known (e.g. not returned by the server), this field is set to undefined.
     * NOTE: This information is not managed by DirectorySearch service and NOT returned to the client (SDK, UCaaS)
     */
    total: number | undefined;
}

export interface DirectorySearchService {
    /**
     * This API returns the list of directories that can be used for a search by the logged-in user.
     * @returns List of directories usable by the logged-in user.
     */
    getAvailableDirectories(): DirectoryType[];

    /**
     * This API allows to search Rainbow users or contacts in a specified directory.
     * It will finds a users/contacts that match a search criteria applied on names properties; depending on the type of directory.
     * Search options can be specified for
     *  - Managing the number of expected results.
     *  - Activating the extended search mode.
     *    This mode allows to apply the search on more extensive names fields (than first and last names); depending on the type of directory.
     *  - Refining the search the search by including an additional company criterion to include or exclude users/contacts of a company.
     *    This option is only available for certain directories.
     *
     * As a result, the search will return
     *  - An array of results (as User model) depending on the number parameter.
     *  - A flag indicating whether more results are available.
     *
     * Notes:
     *  - A search always returns the first N results found.
     *    Results are not paginated, and it is not possible to specify an offset.
     *  - The total number of results is not available.
     * @param searchPattern - The search criteria
     * @param directory - The type of directory to be searched
     * @param options - Options for refining the search and specifying the results to be provided.
     * @throws Error - In the event of a problem with the options supplied (e.g. their value) or encountered during query processing.
     */
    searchByName(searchPattern: string, directory: DirectoryType, options?: DirectorySearchOptions): Promise<DirectorySearchResults>;
}

/**
 * @internal
 */
export class DirectorySearchServiceRB extends Service implements DirectorySearchService {
    // Services
    private mainService: MainService = MainService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private directoryService: DirectoryService = DirectoryService.getInstance();
    private office365AdService: Office365AdService = undefined;
    private personalDirectoryService: PersonalDirectoryService = undefined;
    private companyDirectoryService: CompanyDirectoryService = undefined;
    private phonebookService: PhonebookService = undefined;

    private userInDefaultCompany: boolean = false;

    /**
     * Returns the DirectorySearch service singleton
     */
    public static getInstance(): DirectorySearchServiceRB {
        return Service.create(DIRECTORY_SEARCH_SVC, DirectorySearchServiceRB);
    }

    public static build(): DirectorySearchServiceRB {
        return new DirectorySearchServiceRB();
    }

    private constructor() {
        super();
    }

    // ------------------------------------------------------------
    //                      LIFE CYCLE STUFF
    // ------------------------------------------------------------

    public async start() {
        this.logger.info('[DirectorySearchService] === STARTING ===');

        try {
            // Dynamically inject services (should not be here)
            this.office365AdService = this.mainService.inject<Office365AdService>(OFFICE365_AD_SVC);
            this.personalDirectoryService = this.mainService.inject<PersonalDirectoryService>(PERSONAL_DIRECTORY_SVC);
            this.companyDirectoryService = this.mainService.inject<CompanyDirectoryService>(COMPANY_DIRECTORY_SVC);
            this.phonebookService = this.mainService.inject<PhonebookService>(PHONEBOOK_SVC);

            // Defines if the user performing searcg request is in the Default Company or not
            // Note: When in doubt, all criteria on this point are checked
            if (this.contactService.userContact.company.name === "Default"
                || this.contactService.userContact.company.name === "Rainbow"
                || this.contactService.userContact.isInDefaultCompany) {
                this.userInDefaultCompany = true;
            }

            this.logger.info(`[DirectorySearchService] === STARTED ===`);
        }
        catch (error) {
            this.logger.error(`[DirectorySearchService] === STARTING FAILURE === ${error.message}`);
        }
    }

    public async stop() {
        this.logger.info('[DirectorySearchService] === STOPPING ===');

        // Reset the  data
        this.userInDefaultCompany = false;

        this.logger.info('[DirectorySearchService] === STOPPED ===');
    }

    // ------------------------------------------------------------
    //                      PRIVATE METHODS
    // ------------------------------------------------------------

    /**
     * Check the options (if present) specified in the Directory search request
     * @param directory - The type of directory to be searched
     * @param options - The request options to check
     * @throws Error - In the event of a problem with the options supplied, in particular their value.
     */
    private checkRequestOptions(directory: DirectoryType, options: DirectorySearchOptions): void {
        // No option means no check to do
        if (!options)
            return;

        // Check 'limit' option
        if ((options?.limit <= 0) || (options?.limit > SEARCH_LIMIT_MAX)) {
            throw new Error(`searchByName -- Value of 'limit' option (${options.limit}) is not valid !`);
        }

        // Check 'company' option but only if useful and managed for further search Request. Ignored otherwise.
        let optionCompanyUseful: boolean = false;
        if ((directory === DirectoryType.RAINBOW_USERS)
            || (directory === DirectoryType.RAINBOW_BUSINESS_CONTACTS_DIRECTORY)) {
            optionCompanyUseful = true;
        }

        if ((options?.company) && optionCompanyUseful) {
            if (!options?.company?.companyScope) {
                throw new Error(`searchByName -- The company scope option is missing !`);
            }

            // checking the consistency of information with the directory type in which the search will be done
            if (directory === DirectoryType.RAINBOW_BUSINESS_CONTACTS_DIRECTORY
                && options?.company?.companyScope !== CompanyScopeType.MY_COMPANY) {
                throw new Error(`searchByName -- The company scope option (${options.company.companyScope})' does not match the specified directory !`);
            }
        }
    }

    /**
     * Initializes some options with default values
     * Adapt the limit of results to search by changing it to +1.
     * This will allow us to know if there are more results than requested and thus manage the flag 'moreResultsAvailable'
     * in the final response to the SDK client.
     * @param options - The request options to initialze
     */
    private fillInternalSearchOptions(options: DirectorySearchOptions): void {
        // No option means no check to do
        if (!options) options = {};

        // If necessary, set default value for 'limit' option
        if (!options?.limit) {
            options.limit = SEARCH_LIMIT_DEF;
        }

        // IMPORTANT NOTE:
        // Update the limit so that the internal directory service will search for one more result than requested.
        options.limit++;

        // If necessary, set default value for 'extendedMode' option
        if (!options?.extendedMode) { options.extendedMode = false; }
    }

    // ------------------------------------------------------------
    //                      PUBLIC METHODS
    //     Used by internal Services/Components/... or by SDK
    // ------------------------------------------------------------

    /**
     * This API returns the list of directories that can be used for a search by the logged-in user.
     */
    public getAvailableDirectories(): DirectoryType[] {
        const usableDirectories: DirectoryType[] = [];

        // 'My Network', 'Rainbow users', 'Business directory' are always available
        usableDirectories.push(DirectoryType.RAINBOW_USERS_NETWORK);
        usableDirectories.push(DirectoryType.RAINBOW_USERS);
        usableDirectories.push(DirectoryType.RAINBOW_BUSINESS_CONTACTS_DIRECTORY);

        // Directories under user profile: 'Personal directory', 'PBX users', 'Microsoft Azure AD users/Microsoft O365 contacts'
        if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.PERSONAL_DIRECTORY)) {
            usableDirectories.push(DirectoryType.RAINBOW_PERSONAL_CONTACTS_DIRECTORY);
        }

        if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_PHONE_BOOK)) {
            usableDirectories.push(DirectoryType.RAINBOW_PBX_USERS);
        }

        if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MSO365_DIRECTORY_SEARCH)) {
            usableDirectories.push(DirectoryType.MICROSOFT_O365_USERS);
            usableDirectories.push(DirectoryType.MICROSOFT_OUTLOOK_CONTACTS);
        }

        return usableDirectories;
    }

    /**
     * This API allows to search Rainbow users or contacts in a specified directory.
     * It will find users/directory contacts that match a search criteria applied on names properties; depending on the type of directory.
     * Search options can be specified for
     *  - Managing the number of expected results.
     *  - Activating the extended search mode
     *    This mode allows to apply the search on more extensive names fields (than first and last names); depending on the type of directory.
     *  - Refining the search the search by including an additional company criterion to include or exclude users/contacts of a company.
     *    This option is only available for certain directories.
     * As a result, the search will return
     *  - An array of results (as User model) depending on the number parameter.
     *  - A flag indicating whether more results are available.
     * Notes:
     *  - A search always returns the first N results found.
     *    Results are not paginated, and it is not possible to specify an offset.
     *  - The total number of results is not available.
     * @param searchPattern - The search criteria
     * @param directory - The type of directory to be searched
     * @param options - Options for refining the search and specifying the results to be provided.
     * @throws Error - In the event of a problem with the options supplied (e.g. their value) or encountered during query processing.
     */
    public async searchByName(searchPattern: string, directory: DirectoryType, options?: DirectorySearchOptions): Promise<DirectorySearchResults> {
        try {
            // NOTE: If an error is detected in the search parameters, an exception is thrown and the process stops.
            // No pattern search specified, not useful to continue !
            if (!searchPattern?.length) {
                throw new Error(`searchByName -- No search pattern specified !`);
            }

            // Preliminary check of option values
            try {
                this.checkRequestOptions(directory, options);
            } catch (error) {
                this.logger.error(`[DirectorySearchService]searchByName -- Check options failed: ${error.message}`);
                throw error;
            }

            // Initialize and adapt the options for internal directory search
            const searchOptions: DirectorySearchOptions = { ...options };
            this.fillInternalSearchOptions(searchOptions);

            // Let's go for the search !
            const searchResults: DirectorySearchResults = { users: [], moreResultsAvailable: false };
            let internalSearchResults: DirectoryInternalSearchResults;

            // NOTE: Checking directory user rights does not trigger an exception.
            // If user does not have the right to use a directory, only an error is logged and empty result returned.
            // This avoids problems in the Web Client where searches are sometimes sequenced in a promise.all(...)
            switch (directory) {
                // Search in my Network
                case DirectoryType.RAINBOW_USERS_NETWORK:
                    internalSearchResults = this.contactService.searchRainbowUsersNetwork(searchPattern, searchOptions);
                    break;

                // Search Rainbow users
                case DirectoryType.RAINBOW_USERS:
                    if (!this.directoryService) throw new Error(`directoryService not available`);

                    internalSearchResults = await this.directoryService.searchRainbowUsers(searchPattern, searchOptions);
                    break;

                // Search contacts in user's Personal Directory
                case DirectoryType.RAINBOW_PERSONAL_CONTACTS_DIRECTORY:
                    if (!this.personalDirectoryService) throw new Error(`personalDirectoryService not available`);

                    // A user of a default company cannot use this directory , the search request is rejected.
                    if (this.userInDefaultCompany) {
                        this.logger.error(`[DirectorySearchService] searchByName -- User in default company are not allowed to use the Personal Contact directory`);
                        return { users: [], moreResultsAvailable: false };
                    }

                    // If user does not have licence related to this directory, the search request is rejected.
                    if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.PERSONAL_DIRECTORY)) {
                        this.logger.error(`[DirectorySearchService] searchByName -- User does not have the profile feature PERSONAL_DIRECTORY to use this directory`);
                        return { users: [], moreResultsAvailable: false };
                    }

                    internalSearchResults = await this.personalDirectoryService?.searchPersonalContacts(searchPattern, searchOptions);
                    break;

                // Search contacts Business Directory of users'company or several companies
                case DirectoryType.RAINBOW_BUSINESS_CONTACTS_DIRECTORY:
                    if (!this.companyDirectoryService) throw new Error(`companyDirectoryService not available`);

                    // A user of a default company cannot use this directory , the search request is rejected.
                    if (this.userInDefaultCompany) {
                        this.logger.error(`[DirectorySearchService] searchByName -- User in default company are not allowed to use the Business Contact directory`);
                        return { users: [], moreResultsAvailable: false };
                    }
                    internalSearchResults = await this.companyDirectoryService?.searchBusinessContacts(searchPattern, searchOptions);
                    break;

                // Search users in PBX phonebook the user is connected to
                case DirectoryType.RAINBOW_PBX_USERS:
                    if (!this.phonebookService) throw new Error(`phonebookService not available`);

                    // A user of a default company cannot use this directory , the search request is rejected.
                    if (this.userInDefaultCompany) {
                        this.logger.error(`[DirectorySearchService] searchByName -- User in default company are not allowed to use the PBX Phonebook`);
                        return { users: [], moreResultsAvailable: false };
                    }

                    // If user does not have licence related to this directory, the search request is rejected.
                    if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_PHONE_BOOK)) {
                        this.logger.error(`[DirectorySearchService] searchByName -- User does not have the profile feature TELEPHONY_PHONE_BOOK to use this directory`);
                        return { users: [], moreResultsAvailable: false };
                    }

                    try {
                        internalSearchResults = await this.phonebookService?.searchPBXUsers(searchPattern, searchOptions);
                    } catch (error) {
                        const errorInfo = await this.errorHelperService.getErrorInfo(error, 'searchByName');
                        let translatedMessage: string = "";

                        if (errorInfo?.error?.translatedMessage) {
                            translatedMessage = `- ${errorInfo?.error?.translatedMessage}`;
                        }
                        this.logger.error(`[DirectorySearchService] searchPBXUsers error: ${errorInfo.message} ${translatedMessage}`);
                        return { users: [], moreResultsAvailable: false };
                    }
                    break;

                // Search in Microsoft O365 contacts/AD users configured for users's company
                case DirectoryType.MICROSOFT_O365_USERS:
                    if (!this.office365AdService) throw new Error(`office365AdService not available`);

                    // A user of a default company cannot use this directory , the search request is rejected.
                    if (this.userInDefaultCompany) {
                        this.logger.error(`[DirectorySearchService] searchByName -- User in default company are not allowed to use the O365/AD directory`);
                        return { users: [], moreResultsAvailable: false };
                    }

                    // If user does not have licence related to this directory, the search request is rejected.
                    if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MSO365_DIRECTORY_SEARCH)) {
                        this.logger.error(`[DirectorySearchService] searchByName -- User does not have the profile feature MSO365_DIRECTORY_SEARCH to use this directory`);
                        return { users: [], moreResultsAvailable: false };
                    }

                    try {
                        internalSearchResults = await this.office365AdService?.searchO365usersADcontacts(searchPattern, searchOptions);
                    } catch (error) {
                        const errorInfo = await this.errorHelperService.getErrorInfo(error, 'searchByName');
                        let translatedMessage: string = "";

                        if (errorInfo?.error?.translatedMessage) {
                            translatedMessage = `- ${errorInfo?.error?.translatedMessage}`;
                        }
                        this.logger.error(`[DirectorySearchService] searchO365usersADcontacts error: ${errorInfo.message} ${translatedMessage}`);
                        return { users: [], moreResultsAvailable: false };
                    }
                    break;

                // Search in Microsoft Outlook personal contacts of the connected user
                case DirectoryType.MICROSOFT_OUTLOOK_CONTACTS:
                    if (!this.office365AdService) throw new Error(`office365AdService not available`);

                    // A user of a default company cannot use this directory , the search request is rejected.
                    if (this.userInDefaultCompany) {
                        this.logger.error(`[DirectorySearchService] searchByName -- User in default company are not allowed to use the Outlook contacts`);
                        return { users: [], moreResultsAvailable: false };
                    }

                    // If user does not have licence related to this directory, the search request is rejected.
                    if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MSO365_DIRECTORY_SEARCH)) {
                        this.logger.error(`[DirectorySearchService] searchByName -- User does not have the profile feature MSO365_DIRECTORY_SEARCH to use Outlook contacts`);
                        return { users: [], moreResultsAvailable: false };
                    }

                    try {
                        internalSearchResults = await this.office365AdService?.searchOutlookPersonalcontacts(searchPattern);
                    } catch (error) {
                        const errorInfo = await this.errorHelperService.getErrorInfo(error, 'searchByName');
                        let translatedMessage: string = "";

                        if (errorInfo?.error?.translatedMessage) {
                            translatedMessage = `- ${errorInfo?.error?.translatedMessage}`;
                        }
                        this.logger.error(`[DirectorySearchService] searchOutlookPersonalcontacts error: ${errorInfo.message} ${translatedMessage}`);
                        return { users: [], moreResultsAvailable: false };
                    }
                    break;

                default:
                    this.logger.error(`[DirectorySearchService] searchByName -- Unknown Directory ${directory} !`);
                    return { users: [], moreResultsAvailable: false };
            }

            // Check if there is more results that requested
            // '-1' done to retrieve the initial or default limit value specified in the request.
            const nbRequestedResults = searchOptions.limit - 1;
            if (internalSearchResults.contacts.length > nbRequestedResults) {
                searchResults.moreResultsAvailable = true;
            }

            searchResults.users = internalSearchResults.contacts.slice(0, nbRequestedResults);

            return searchResults;
        }

        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'searchByName');
            this.logger.error(`[DirectorySearchService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

}
